import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Row,
  Col,
  message,
  Spin,
  Alert,
  Segmented,
  Upload,
  Result,
} from 'antd';
import type { UploadProps } from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  BookOutlined,
  RobotOutlined,
  InboxOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { mekakApi } from './services';
import type { KnowledgeCreateRequest } from './models';
import { useAuth } from '../../contexts/AuthContext';
import { useFileUpload } from './handlers';

interface KnowledgeFormProps {
  mode: 'create' | 'edit';
}

const KnowledgeForm: React.FC<KnowledgeFormProps> = ({ mode }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [inputMode, setInputMode] = useState<'text' | 'file'>('text');
  const { uploading, uploadResult, handleUpload } = useFileUpload();

  const isEdit = mode === 'edit';
  const pageTitle = isEdit ? '編輯知識條目' : '新增知識條目';

  const fetchKnowledge = async () => {
    if (!isEdit || !id) return;

    try {
      setLoading(true);
      const response = await mekakApi.getKnowledge(id);
      const data = response.data.data;

      form.setFieldsValue({
        content: data.content,
        tags: data.tags ?? [],
      });
    } catch (error) {
      message.error('獲取知識條目失敗');
      navigate('/mekak');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKnowledge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const handleSubmit = async (values: { content: string; tags?: string[] }) => {
    try {
      setSubmitLoading(true);

      const payload: KnowledgeCreateRequest = {
        content: values.content.trim(),
        author: user?.userName || 'user',
        tags: values.tags ?? [],
      };

      if (isEdit && id) {
        await mekakApi.updateKnowledge(id, payload);
        message.success('知識條目更新成功');
      } else {
        await mekakApi.createKnowledge(payload);
        message.success('知識條目建立成功，AI 正在進行預處理與向量化');
      }

      navigate('/mekak');
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : '操作失敗，請稍後再試';
      message.error(isEdit ? `更新失敗：${errorMessage}` : `建立失敗：${errorMessage}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/mekak');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64 py-16">
        <Spin size="large" tip="載入中..." />
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Page header */}
      <Card className="mb-4" style={{ borderRadius: 8 }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Space align="center">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleCancel}
                type="text"
                size="large"
              />
              <BookOutlined style={{ fontSize: 20, color: '#1677ff' }} />
              <h2 className="m-0 text-xl font-semibold">{pageTitle}</h2>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Mode selector (only show in create mode) */}
      {!isEdit && (
        <Card className="mb-4" style={{ borderRadius: 8 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <span style={{ fontWeight: 500 }}>輸入方式</span>
            <Segmented
              options={[
                { label: '手動輸入', value: 'text', icon: <BookOutlined /> },
                { label: '檔案上傳', value: 'file', icon: <FileOutlined /> },
              ]}
              value={inputMode}
              onChange={(val) => setInputMode(val as 'text' | 'file')}
              size="large"
            />
          </Space>
        </Card>
      )}

      {/* AI preprocessing notice */}
      {!isEdit && inputMode === 'text' && (
        <Alert
          className="mb-4"
          icon={<RobotOutlined />}
          message="AI 自動預處理說明"
          description="提交後，系統將自動使用 Gemini AI 對內容進行智能切割與摘要，並生成向量嵌入以提升搜尋準確率。處理時間約 3-10 秒，完成後即可透過自然語言搜尋此條目。標題會由系統自動擷取內容首行產生。"
          type="info"
          showIcon
        />
      )}

      {/* File upload mode */}
      {!isEdit && inputMode === 'file' && (
        <Card style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Alert
            className="mb-4"
            icon={<RobotOutlined />}
            message="檔案上傳說明"
            description="上傳 PPTX 或 PDF 檔案後，系統會自動抽取文字、智慧合併短頁面，並透過 AI 切割成知識點。每個知識點都會生成向量嵌入，可透過自然語言搜尋。"
            type="info"
            showIcon
          />
          {uploadResult ? (
            <Result
              status="success"
              title="檔案處理完成"
              subTitle={`${uploadResult.original_name} — ${uploadResult.page_count} 頁，產生 ${uploadResult.knowledge_count} 個知識點`}
              extra={[
                <Button key="back" onClick={() => navigate('/mekak')}>
                  返回知識列表
                </Button>,
                <Button key="detail" type="primary" onClick={() => navigate(`/mekak/files/${uploadResult.file_document_id}`)}>
                  查看知識點
                </Button>,
              ]}
            />
          ) : (
            <Upload.Dragger
              name="file"
              multiple={false}
              accept=".pptx,.pdf"
              showUploadList={false}
              customRequest={({ file }) => {
                handleUpload(
                  file as File,
                  () => {},
                  () => {},
                );
              }}
              disabled={uploading}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {uploading ? '處理中...' : '點擊或拖曳檔案到此處上傳'}
              </p>
              <p className="ant-upload-hint">
                支援 .pptx 和 .pdf 檔案
              </p>
            </Upload.Dragger>
          )}
        </Card>
      )}

      {/* Text input mode (original form) */}
      {(isEdit || inputMode === 'text') && (
        <Card style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark="optional"
            scrollToFirstError
          >
            <Form.Item
              label="內容"
              name="content"
              rules={[
                { required: true, message: '請輸入內容' },
                { whitespace: true, message: '內容不能為空白' },
              ]}
            >
              <Input.TextArea
                placeholder="請詳細描述此知識條目的內容。內容越詳細，AI 搜尋結果越準確。&#10;系統會自動以內容的第一行作為標題。"
                rows={10}
                showCount
                allowClear
                style={{ resize: 'vertical' }}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={16} md={12}>
                <Form.Item
                  label="標籤"
                  name="tags"
                  extra="輸入標籤名稱後按 Enter 建立，可新增多個標籤"
                >
                  <Select
                    mode="tags"
                    placeholder="輸入並按 Enter 新增標籤"
                    tokenSeparators={[',', '，']}
                    allowClear
                    style={{ width: '100%' }}
                    maxTagCount="responsive"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item className="mt-6">
              <Space size="middle">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitLoading}
                  icon={<SaveOutlined />}
                  size="large"
                >
                  {isEdit ? '更新條目' : '建立條目'}
                </Button>
                <Button size="large" onClick={handleCancel}>
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      )}
    </div>
  );
};

export default KnowledgeForm;
